import { SceneNode } from './SceneNode';
import { RotationAnimation, WiggleAnimation } from '../animations';
import { SimpleSettingsProperty, InterpolatedProperty } from '../properties';
import type { Mesh3D } from '../shapes/Mesh3D';

export class SceneNodeAnimator {
    private readonly _target: SceneNode;
    private _rotationAnimation: RotationAnimation | null = null;
    private _wiggleAnimation: WiggleAnimation | null = null;

    // --- Animator's own control properties ---
    public readonly enableBillboard: SimpleSettingsProperty<boolean>;
    public readonly enableRotation: SimpleSettingsProperty<boolean>;
    public readonly enableWiggle: SimpleSettingsProperty<boolean>;
    public readonly rotateSpeed: InterpolatedProperty<number>;
    public readonly wiggleSpeed: InterpolatedProperty<number>;
    public readonly wiggleHeight: InterpolatedProperty<number>;
    // Wiggle axes - making these configurable
    public readonly wiggleX: SimpleSettingsProperty<boolean>;
    public readonly wiggleY: SimpleSettingsProperty<boolean>;
    public readonly wiggleZ: SimpleSettingsProperty<boolean>;

    constructor(target: SceneNode) {
        this._target = target;
        
        // --- Initialize Control Properties with Defaults ---
        const group = 'Animation';
        this.enableBillboard = new SimpleSettingsProperty('Shape Billboard Mode', null, false, group);
        this.enableRotation = new SimpleSettingsProperty('Enable Rotation', null, false, group);
        this.enableWiggle = new SimpleSettingsProperty('Enable Wiggle', null, false, group);
        this.rotateSpeed = new InterpolatedProperty('Rotate Speed', null, 1, 0.5, group).withBounds(0, 100, 0.1);
        this.wiggleSpeed = new InterpolatedProperty('Wiggle Speed', null, 1, 0.5, group).withBounds(0, 10, 0.1);
        this.wiggleHeight = new InterpolatedProperty('Wiggle Height', null, 0.2, 0.5, group).withBounds(0, 2, 0.05);
        this.wiggleX = new SimpleSettingsProperty('Wiggle X', null, true, group);
        this.wiggleY = new SimpleSettingsProperty('Wiggle Y', null, true, group);
        this.wiggleZ = new SimpleSettingsProperty('Wiggle Z', null, false, group);

        // --- Listen to our OWN properties to control the animations ---
        this.connectAnimationsToProperties();
    }

    private getRotationAnimation(): RotationAnimation {
        if (!this._rotationAnimation) {
        this._rotationAnimation = new RotationAnimation();
            this._rotationAnimation.setTarget(this._target);
        }
        return this._rotationAnimation;
    }

    private getWiggleAnimation(): WiggleAnimation {
        if (!this._wiggleAnimation) {
            this._wiggleAnimation = new WiggleAnimation();
            this._wiggleAnimation.setTarget(this._target);
            this._wiggleAnimation.setWigglePattern('vertical'); // A sensible default
        }
        return this._wiggleAnimation;
    }

    private connectAnimationsToProperties(): void {
        this.enableRotation.on(enabled => {
            if (enabled) {
                this.getRotationAnimation().start();
            } else {
                this._rotationAnimation?.stop();
            }
        });

        this.enableWiggle.on(enabled => {
            if (enabled) {
                this.getWiggleAnimation().start();
            } else {
                this._wiggleAnimation?.stop();
            }
        });
    }

    public update(deltaTime: number): void {
        // --- Handle Billboarding ---
        // Use duck typing instead of instanceof to avoid circular dependency
        if ('billboardEnabled' in this._target) {
            (this._target as any).billboardEnabled = this.enableBillboard.value ?? false;
        }
        
        // --- Update Animations ---
        if (this._rotationAnimation?.isRunning()) {
            this._rotationAnimation.setRotationSpeed(this.rotateSpeed.value ?? 1);
            this._rotationAnimation.update(deltaTime);
        }
        if (this._wiggleAnimation?.isRunning()) {
            const wiggleAnim = this.getWiggleAnimation();
            wiggleAnim.setSpeed(this.wiggleSpeed.value ?? 1);
            wiggleAnim.setAmplitude(this.wiggleHeight.value ?? 0.2);
            // Pass axis config
            wiggleAnim.wiggleX = this.wiggleX.value ?? true;
            wiggleAnim.wiggleY = this.wiggleY.value ?? true;
            wiggleAnim.wiggleZ = this.wiggleZ.value ?? false;
            wiggleAnim.update(deltaTime);
        }
    }
} 